import React, {
	useState,
	useMemo,
	useEffect,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { SHOT_THUMBNAIL_TYPES } from '../../stores/reducers/shots';
import * as VideoActions from '../../stores/actions/video';
import TitleCard from '../general-components/title-card';
import usePrevious from '../../utils/use-previous';
import { setVideoTitlesHaveFired } from '../../stores/actions/shots';
import useShots from '../../contexts/shots/provider';
import { ShotDirection } from '../../hooks/use-shots';

interface OwnProps {
	idOfPresenter: number;
	thumbnailType?: number | null;
	shotId: number;
	videoTitlesOn: boolean;
}

const ANIMATION_STATES = {
	OFF: 'off',
	STARTING: 'starting',
	AUTO_OUT: 'auto-out',
	MANUAL_OUT: 'manual-out',
	COMPLETE: 'complete',
};

export default function Title(props: OwnProps) {
	const {
		idOfPresenter,
		thumbnailType,
		shotId,
		videoTitlesOn,
	} = props;

	const hiddenTitles = useSelector((state: GreenroomState) => state.VideoReducer.hiddenTitles);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const currentShotId = useSelector((state: GreenroomState) => state.ShotsReducer.currentShotId);
	const videoTitlesHaveFired = useSelector(
		(state: GreenroomState) => state.ShotsReducer.videoTitlesHaveFired,
	);
	const {
		titlesAnimation,
		titlesDuration,
		titlesPosition,
		titlesSelectedCard,
	} = useSelector((state: GreenroomState) => state.ShotsReducer.canvasStyle);

	const dispatch = useDispatch();

	const [animationState, setAnimationState] = useState<string>(ANIMATION_STATES.OFF);
	const [useHiddenTitles, setUseHiddenTitles] = useState<boolean>(true);
	const [fontsLoaded, setFontsLoaded] = useState<boolean>(true);
	const animationMap = new Map([[Infinity || null, ''], [5, 'five'], [10, 'ten']]);
	const titleRef = useRef<HTMLDivElement | null>(null);

	const { shotDirection } = useShots();

	const userObjectForThisTitle = useMemo(
		() => connectedUsers.find((user) => user.gr_user === idOfPresenter && user.in_room),
		[idOfPresenter, connectedUsers],
	);

	const shotVideoTitlesOn = useMemo(() => {
		if (
			thumbnailType === SHOT_THUMBNAIL_TYPES.SHOWBOARD
			|| thumbnailType === SHOT_THUMBNAIL_TYPES.TELEPROMPTER
		) {
			return videoTitlesOn && currentShotId === shotId;
		}
		return videoTitlesOn;
	}, [currentShotId, videoTitlesOn, thumbnailType, shotId]);

	const prevTitlesOn = usePrevious(shotVideoTitlesOn);

	useEffect(() => {
		// prevent the component from rendering until web fonts have been loaded
		document.fonts.onloading = () => { setFontsLoaded(false); };
		document.fonts.onloadingdone = () => { setFontsLoaded(true); };
	}, []);

	useEffect(() => {
		// This ensures animatedOutTitles matches hiddenTitles
		// when the component loads (animatedTitles is local state)
		if (!userObjectForThisTitle) return;

		if (
			userObjectForThisTitle
			&& (hiddenTitles?.includes(userObjectForThisTitle.gr_user) || !shotVideoTitlesOn)
		) {
			setAnimationState(ANIMATION_STATES.OFF);
		}

		if (thumbnailType === SHOT_THUMBNAIL_TYPES.SHOTS_TAB) {
			setUseHiddenTitles(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (shotVideoTitlesOn && userObjectForThisTitle && !videoTitlesHaveFired) {
			setAnimationState(ANIMATION_STATES.STARTING);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [titlesAnimation, titlesDuration, titlesPosition, titlesSelectedCard]);

	useEffect(() => {
		// When the first item in the hiddenTitles array is 0, we restart title animations
		const restartTitleAnimation = hiddenTitles[0] === 0
			&& userObjectForThisTitle && shotVideoTitlesOn;

		// Restart only if this title is for the current shot
		if (restartTitleAnimation
			&& currentShotId === shotId
			&& animationState !== ANIMATION_STATES.STARTING
		) {
			setAnimationState(ANIMATION_STATES.OFF);
		}

		// This dispatch only needs to fire for titles in the canvas
		if (restartTitleAnimation && !thumbnailType && animationState) {
			dispatch(VideoActions.setHiddenTitles([]));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hiddenTitles, shotVideoTitlesOn, animationState]);

	useEffect(() => {
		if (!userObjectForThisTitle) return;

		if (shotVideoTitlesOn && animationState === ANIMATION_STATES.OFF) {
			setAnimationState(ANIMATION_STATES.STARTING);
		} else if (
			shotVideoTitlesOn && !prevTitlesOn
			&& (
				animationState === ANIMATION_STATES.MANUAL_OUT
				|| animationState === ANIMATION_STATES.AUTO_OUT
			)
		) {
			setAnimationState(ANIMATION_STATES.OFF);
		} else if (!shotVideoTitlesOn && prevTitlesOn && animationState === ANIMATION_STATES.STARTING) {
			setAnimationState(ANIMATION_STATES.MANUAL_OUT);
		} else if (!shotVideoTitlesOn && animationState === ANIMATION_STATES.COMPLETE) {
			setAnimationState(ANIMATION_STATES.OFF);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shotVideoTitlesOn, animationState, userObjectForThisTitle]);

	function titleDoneAnimating(userId: number, e: React.AnimationEvent) {
		if ((animationState === ANIMATION_STATES.MANUAL_OUT || animationState === ANIMATION_STATES.AUTO_OUT) && (['dissolve-out', 'move-out', 'none-out'].includes(e.animationName))) {
			if (
				animationState !== ANIMATION_STATES.MANUAL_OUT
				&& shotVideoTitlesOn && !hiddenTitles && !thumbnailType
			) {
				dispatch(VideoActions.setHiddenTitles([userId]));
			} else if (
				animationState !== ANIMATION_STATES.MANUAL_OUT && shotVideoTitlesOn
				&& !hiddenTitles.includes(userId) && !thumbnailType
			) {
				dispatch(VideoActions.setHiddenTitles([...hiddenTitles as number[], userId]));
			}
			setAnimationState(ANIMATION_STATES.COMPLETE);
		}
	}

	const setVideoTitlesFiredValue = (value: boolean): void => {
		if (videoTitlesHaveFired !== value) {
			if (value) {
				// Not ideal, but need to wait for animation
				setTimeout(() => {
					dispatch(setVideoTitlesHaveFired(value));
				}, 1500);
			} else {
				dispatch(setVideoTitlesHaveFired(value));
			}
		}
	};

	useEffect(() => {
		if ([ShotDirection.Backward, ShotDirection.Forward].includes(shotDirection)) {
			setVideoTitlesFiredValue(false);
		}
	}, [shotDirection]);

	const onTitleAnimationStart = (e: React.AnimationEvent<HTMLDivElement>) => {
		if (e.animationName.includes('out')) {
			setAnimationState(ANIMATION_STATES.AUTO_OUT);

			if (titlesDuration) {
				setVideoTitlesFiredValue(true);
			}
		}
	};

	const titles = useMemo(() => {
		if (!userObjectForThisTitle || animationState === ANIMATION_STATES.OFF || !fontsLoaded) {
			return <></>;
		}

		// if user && we are previewing or user title is not hidden
		if ((shotVideoTitlesOn || animationState === ANIMATION_STATES.STARTING)
		&& (!hiddenTitles?.includes(userObjectForThisTitle.gr_user) || !useHiddenTitles)) {
			// positioning currently only available for basic title (title 3), not svg titles
			const position = titlesSelectedCard === 3 ? titlesPosition : 'bottom-left';

			return (
				<div
					className={classNames(
						'title-overlay',
						position,
						titlesAnimation,
						animationMap.get(titlesDuration),
						{ hidden: videoTitlesHaveFired },
					)}
					ref={titleRef}
					onAnimationStart={(e) => onTitleAnimationStart(e)}
					onAnimationEnd={(e) => titleDoneAnimating(userObjectForThisTitle.gr_user, e)}
				>
					<TitleCard
						user={userObjectForThisTitle}
						cardType={titlesSelectedCard}
						preview={false}
						position={position}
					/>
				</div>
			);
		}

		if (!shotVideoTitlesOn && animationState === ANIMATION_STATES.MANUAL_OUT) {
			// Title is toggled off but hasn't animated out yet.
			// Following code manually animates a title out.
			if (titleRef.current) {
				// Added this code to remove the animation css from the original title
				// (https://stackoverflow.com/questions/21664940/force-browser-to-trigger-reflow-while-changing-css)
				// before re-adding the title with the opposite animation to manually remove it.
				// If the original title had a timed animation then there is some weirdness with adding this
				// title to immedatitely animate it out and some leftover animation styling existing from
				// the original title. This clears the original title animation styling which is necessary.
				titleRef.current.style.animation = 'none';
				// eslint-disable-next-line
				titleRef.current.offsetHeight; /* trigger reflow */
				titleRef.current.style.animation = '';
			}

			const position = titlesSelectedCard === 3 ? titlesPosition : 'bottom-left';

			return (
				<div className={classNames(
					'title-overlay',
					position,
					{
						'dissolve-out': titlesAnimation === 'dissolve',
						'move-out': titlesAnimation === 'move-in',
						'none-out': titlesAnimation === 'none',
					},
				)}
				onAnimationEnd={(e) => titleDoneAnimating(userObjectForThisTitle?.gr_user, e)}
				>
					<TitleCard
						user={userObjectForThisTitle}
						cardType={titlesSelectedCard}
						preview={false}
					/>
				</div>
			);
		}
		return <></>;
	}, [
		userObjectForThisTitle,
		shotVideoTitlesOn,
		animationState,
		titlesSelectedCard,
		titlesPosition,
		titlesAnimation,
		titlesDuration,
		videoTitlesHaveFired,
		fontsLoaded,
		hiddenTitles,
	]);

	return (
		<>
			{titles}
		</>
	);
}
